import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductModel, ProductOptionModel } from '../../model';
import { ProductImage } from '../ProductImage/ProductImage';
import styles from './ProductDetails.module.scss';

import { calcItemPrice, cartStore } from '@/entities/cart/store/cartStore';
import { productsStore } from '@/entities/product/store/productsStore';
import { formatPrice } from '@/shared/lib/currency';
import { observer } from '@/shared/lib/signal/observer';
import { AddToCartButton } from '@/shared/ui-kit/AddToCartButton/AddToCartButton';
import { Button } from '@/shared/ui-kit/Button/Button';
import { ButtonsGroup } from '@/shared/ui-kit/ButtonsGroup/ButtonsGroup';
import { Checkbox } from '@/shared/ui-kit/Checkbox/Checkbox';
import { InnerHtml } from '@/shared/ui-kit/InnerHtml/InnerHtml';
import { useToast } from '@/shared/ui-kit/ToastProvider/useToast';

type Props = {
  product: ProductModel;
  onClose: () => void;
};
export const ProductDetails = observer(({ product, onClose }: Props) => {
  const [qty, setQty] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const productVariants = productsStore.getProductVariants(product);

  const [selectedOptions, setSelectedOptions] = useState<ProductOptionModel[]>(
    []
  );
  const { showError } = useToast();

  const initialVariantId = useMemo(() => {
    const byId = productVariants.find((v) => v.id === product.id);
    if (byId) return byId.id;

    const byParam = product.parameter
      ? productVariants.find((v) => v.parameter === product.parameter)
      : undefined;
    if (byParam) return byParam.id;

    return productVariants[0]?.id ?? 0;
  }, [product.id, product.parameter, productVariants]);

  const [selectedProductId, setSelectedProductId] =
    useState<number>(initialVariantId);
  const selectedProduct = productsStore.findProductById(selectedProductId);

  useEffect(() => {
    setSelectedProductId(initialVariantId);
    setSelectedOptions([]);
  }, [initialVariantId]);

  const handleClose = useCallback(() => {
    setQty(1);
    onClose();
  }, [onClose]);

  const mappedProductOptions = useMemo(
    () =>
      selectedOptions.map((opt) => ({
        quantity: qty,
        optionName: opt.name,
        optionId: opt.optionId,
        price: opt.price ?? 0,
      })),
    [qty, selectedOptions]
  );

  const handleAddToCart = useCallback(() => {
    if (!selectedProduct.value) {
      showError('Не выбран вариант товара');
      return;
    }
    if (isAdding) return;
    setIsAdding(true);

    cartStore
      .add(selectedProduct.value, qty, mappedProductOptions)
      .then(handleClose)
      .catch(() => showError('Ошибка добавления в корзину'))
      .finally(() => setIsAdding(false));
  }, [
    isAdding,
    selectedProduct.value,
    qty,
    mappedProductOptions,
    showError,
    handleClose,
  ]);

  const totalPrice = selectedProduct.value
    ? calcItemPrice({
        productId: selectedProduct.value.id,
        menuId: selectedProduct.value.menuId,
        qty,
        options: mappedProductOptions,
      })
    : 0;

  const v = selectedProduct.value;
  const hasCompound = [v?.kcal, v?.proteins, v?.fats, v?.carbohydrates].some(
    (x) => x != null && Number(x) > 0
  );

  const renderVal = (val: number | null | undefined, unit = '') =>
    val != null && Number(val) > 0 ? `${val}${unit}` : '—';

  if (!selectedProduct.value) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <ProductImage
          className={styles.image}
          src={selectedProduct.value.image}
        />
        <div className={styles.titleWrapper}>
          <h1 className={styles.title}>{selectedProduct.value.name}</h1>
          {selectedProduct.value.description && (
            <InnerHtml
              className={styles.description}
              html={selectedProduct.value.description}
            />
          )}
        </div>
        {productVariants.length > 1 ? (
          <div className={styles.buttonsWrapper}>
            <ButtonsGroup>
              {productVariants.map(({ id, parameter, price }) => (
                <button
                  key={id}
                  type="button"
                  data-id={id}
                  data-active={id === selectedProductId}
                  className={styles.button}
                  onClick={() => setSelectedProductId(id)}
                >
                  {parameter}
                  <span className={styles.subLabel}>{formatPrice(price)}</span>
                </button>
              ))}
            </ButtonsGroup>
          </div>
        ) : (
          <div className={styles.buttonsWrapper}>
            <span className={styles.singleParameter}>
              {productVariants[0]?.parameter}{' '}
            </span>
          </div>
        )}
      </div>

      {selectedProduct.value.options &&
        selectedProduct.value.options.length > 0 && (
          <div className={styles.additionalsContainer}>
            <h1 className={styles.additionalsTitle}>Дополнительно</h1>
            <div className={styles.additionalsRow}>
              {selectedProduct.value.options.map((opt) => (
                <div key={opt.optionId} className={styles.additionalItem}>
                  <Checkbox
                    classNames={{
                      checkbox: styles.checkbox,
                      label: styles.checkboxLabel,
                    }}
                    checked={
                      !!selectedOptions.find((o) => o.optionId === opt.optionId)
                    }
                    onChange={(value) => {
                      if (value) {
                        setSelectedOptions((prev) => [...prev, opt]);
                      } else {
                        setSelectedOptions((prev) =>
                          prev.filter((o) => o.optionId !== opt.optionId)
                        );
                      }
                    }}
                  >
                    <span>{opt.name}</span>
                    <span className={styles.optionPrice}>
                      {opt.price != null ? `+${formatPrice(opt.price)}` : ''}
                    </span>
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
        )}

      {selectedProduct.value.composition && (
        <div className={styles.descriptionWrapper}>
          <h2 className={styles.descriptionTitle}>Состав</h2>
          <InnerHtml
            className={styles.description}
            html={selectedProduct.value.composition}
          />
        </div>
      )}

      {hasCompound && (
        <div className={styles.compound}>
          <div className="row text-center">
            <div className="col">
              <div className={styles.compoundValue}>{renderVal(v?.kcal)}</div>
              <div className={styles.compoundLabel}>Ккал</div>
            </div>
            <div className="col">
              <div className={styles.compoundValue}>
                {renderVal(v?.proteins, 'г')}
              </div>
              <div className={styles.compoundLabel}>Белки</div>
            </div>
            <div className="col">
              <div className={styles.compoundValue}>
                {renderVal(v?.fats, 'г')}
              </div>
              <div className={styles.compoundLabel}>Жиры</div>
            </div>
            <div className="col">
              <div className={styles.compoundValue}>
                {renderVal(v?.carbohydrates, 'г')}
              </div>
              <div className={styles.compoundLabel}>Углеводы</div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <AddToCartButton
          size="M"
          onChange={setQty}
          value={qty}
          className={styles.addToCart}
        />
        <Button
          type="button"
          className={styles.buttonToCart}
          onClick={handleAddToCart}
          disabled={isAdding}
        >
          {formatPrice(totalPrice)} в корзину
        </Button>
      </div>
    </div>
  );
});
